// Sub-tile block damage. A chipped tile carries a 6×6 grid of sub-cell HITPOINTS: each of
// its 36 sub-cells has health from MAXHP (pristine) down to 0 (gone). A whole tile stores
// nothing (the common case); a tile whose sub-cells are all at 0 is destroyed and the
// caller turns it to AIR and forgets the grid, so a destroyed block costs nothing.
//
// HP rather than a plain present/absent bit is what lets a hit CHIP a block gradually: a
// sub-cell fades (its opacity tracks its health) as it's worn down, then pops out at 0. So
// every weapon can scuff terrain a little without instantly punching holes, and a block
// visibly weakens before it breaks.

import { TILE } from "./chunks.js";

export const SUB = 6;                 // sub-cells per tile edge
export const SUBTILE = TILE / SUB;    // world size of one sub-cell
export const CELLS = SUB * SUB;       // 36
export const MAXHP = 100;             // a pristine sub-cell's health

const idx = (col, row) => row * SUB + col;

// A pristine tile's grid — every sub-cell at full health.
export const newCells = () => new Array(CELLS).fill(MAXHP);

export const hpAt = (cells, col, row) => cells[idx(col, row)];
export const present = (cells, col, row) => cells[idx(col, row)] > 0;
export const anyPresent = (cells) => cells.some((v) => v > 0);
export function countPresent(cells) {
    let n = 0;
    for (const v of cells) if (v > 0) n++;
    return n;
}

// A sub-cell's draw opacity: full health is solid, and it fades toward (but never below) a
// floor as it's damaged, so even a nearly-dead cell stays visible right up until it pops.
export const MIN_OPACITY = 0.15;
export const opacityOf = (hp) => (hp <= 0 ? 0 : MIN_OPACITY + (1 - MIN_OPACITY) * (hp / MAXHP));

// Which sub-cell a world point falls in, given its tile's top-left world corner. Clamped,
// so a point exactly on the far edge lands in the last cell rather than off the grid.
export function subOf(x, y, tileX0, tileY0) {
    const col = Math.min(SUB - 1, Math.max(0, Math.floor((x - tileX0) / SUBTILE)));
    const row = Math.min(SUB - 1, Math.max(0, Math.floor((y - tileY0) / SUBTILE)));
    return { col, row };
}

// Deal `amount` damage to every sub-cell of `cells` whose CENTRE lies within `radius` of
// (cx, cy). Mutates the array; returns whether anything actually changed. `tileX0,tileY0`
// is the tile's world corner.
export function damageCircle(cells, tileX0, tileY0, cx, cy, radius, amount) {
    const r2 = radius * radius;
    let changed = false;
    for (let row = 0; row < SUB; row++) {
        for (let col = 0; col < SUB; col++) {
            const i = idx(col, row);
            if (cells[i] <= 0) continue;
            const sx = tileX0 + (col + 0.5) * SUBTILE;
            const sy = tileY0 + (row + 0.5) * SUBTILE;
            const dx = sx - cx, dy = sy - cy;
            if (dx * dx + dy * dy <= r2) { cells[i] = Math.max(0, cells[i] - amount); changed = true; }
        }
    }
    return changed;
}
